import React, {useEffect, useState} from 'react';
import './App.css';

import {Routes, Route, useNavigate} from 'react-router-dom';
import Settings from './components/SettingsPage/Settings';
import Contact from './components/ContactPage/Contact';
import Dashboard from './components/Dashboard/Dashboard'
import DwFooter from "./components/DwFooter";
import Transactions from "./components/Transactions/Transactions";
import Cookies from "js-cookie";
import RegistrationPage from "./components/RegistrationPage/RegistrationPage";
import LandingPage from "./components/LandingPage/LandingPage";
import {AuthContext} from "./context/authContext";
import ProtectedRoute from "./context/ProtectedRoute";
import {UserContext} from "./context/userContext";
import {AccountContext} from "./context/accountContext";
import {appConfig, clearCookies, getTokenHeader} from "./utils/settings";
import ReactGA from "react-ga4";
import Profile from "./components/AppDrawer/AccountMenu/Profile";
import Priorities from "./components/Priorities/Priorities";
import AccountFamily from "./components/AccountFamily/AccountFamily";
import PrivacyPolicyPage from "./components/Legal/privacy-policy";
import LoginPage from "./components/LandingPage/LoginPage";
import MortgageCalculator from "./components/Calculators/MortgageCalculator";
import EULA from "./components/Legal/eula";
import PasswordResetPage from "./components/LandingPage/PasswordResetPage";


export default function App(props) {
   const navigate = useNavigate();
   const currentDate = new Date();
   const existingToken = Cookies.get("token");
   const [isLoggedIn, setIsLoggedIn] = useState(false);
   const [passwordExpired, setPasswordExpired] = useState(false);
   const [authToken, setAuthToken] = useState(existingToken);
   const [user, setUser] = useState(null);
   const [userLoading, setUserLoading] = useState(true);
   const [accountUsers, setAccountUsers] = useState([]);
   const [accountFamily, setAccountFamily] = useState([]);
   const [accountUsersLoading, setAccountUsersLoading] = useState(true);
   const [accountFamilyLoading, setAccountFamilyLoading] = useState(true);
   const [accountCategories, setAccountCategories] = useState([]);
   const [loadingAccountCategories, setLoadingAccountCategories] = useState(true);
   const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
   const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
   const [reloading, setReloading] = useState(true);

   ReactGA.initialize("G-3M9XM42PZ1");

    // Context is lost over app refresh. Reset from cookie if it exists.
    useEffect(() => {
        if(reloading) {
            // Update the document title using the browser API
            const userCookie = Cookies.get("user");
            if(userCookie) {
                setUser(JSON.parse(atob(userCookie)));
                setIsLoggedIn(true);
                setReloading(false);
                setUserLoading(false);
            }
        }
    });

    // Account category fetching
    useEffect(() => {
       if(isLoggedIn) {
          (async () => {
             const options = {method: "GET", headers: getTokenHeader()};
             const response = await fetch(appConfig.host +'/api/v1/categories', options);
             // Exit and require login
             if(response.status === 401) {
                setPasswordExpired(true);
             }
             const categories = await response.json();
             setLoadingAccountCategories(false);
             setAccountCategories(categories);
          })();
       }
    }, [loadingAccountCategories, isLoggedIn]);

   // Combined Fetch of Account user and account family
   useEffect(() => {
      // Only run if the user is logged in
      if (!isLoggedIn) {
         return;
      }

      let isMounted = true;

      const fetchData = async () => {
         const userOptions = { method: "GET", headers: getTokenHeader() };
         const familyOptions = { method: "GET", headers: getTokenHeader() }; // Assuming same headers

         const usersUrl = `${appConfig.authHost}/api/v1/accounts/${user.accountId}/user/users`;
         const familyUrl = `${appConfig.host}/api/v1/family/`;

         try {
            const [userResponse, familyResponse] = await Promise.all([
               fetch(usersUrl, userOptions),
               fetch(familyUrl, familyOptions)
            ]);

            if (userResponse.status === 401 || familyResponse.status === 401) {
               if (isMounted) {
                  setPasswordExpired(true);
                  setAccountUsersLoading(false);
                  setAccountFamilyLoading(false);
               }
               return;
            }

            // Check if responses are ok (status 200-299) before parsing JSON
            if (!userResponse.ok) {
               throw new Error(`Failed to fetch users: ${userResponse.statusText}`);
            }
            if (!familyResponse.ok) {
               throw new Error(`Failed to fetch family: ${familyResponse.statusText}`);
            }

            const [users, family] = await Promise.all([
               userResponse.json(),
               familyResponse.json()
            ]);

            const fullFamily = family.map(member => {
               return {...member, role: "Member"};
            });
            users.forEach(u => {
               if (u.id && u.firstName) {
                  const userObject = {id: u.id , firstName: u.firstName, role: u.role};
                  fullFamily.push(userObject);
               }
            });


            // If the component is still mounted, update the state
            if (isMounted) {
               console.log(accountFamily);
               console.log(users);

               // Set state as required
               setAccountUsers(users);
               setAccountFamily(fullFamily);

               // Set loading states to false *after* data is processed and set
               setAccountUsersLoading(false);
               setAccountFamilyLoading(false);
            }

         } catch (error) {
            console.error("Error fetching account data:", error);
            if (isMounted) {
               setPasswordExpired(false); // Ensure this is false on generic errors
               setAccountUsersLoading(false); // Turn off loading indicators on error
               setAccountFamilyLoading(false);
            }
         }
      };

      fetchData();

      // Cleanup function: runs when the component unmounts or dependencies change
      return () => {
         isMounted = false; // Set flag to false when component unmounts
      };

   }, [isLoggedIn]);


   // Any fetch that receives a 401 should setPasswordExpired via auth context
   // And redirect user to /login
   useEffect(() => {
      if(passwordExpired) {
         // reset, clear cookies and redirect to login page
         console.log("We should be resetting");
         setIsLoggedIn(false);
         setPasswordExpired(false);
         clearCookies();
         navigate('/login');
      }
   }, [passwordExpired])


    return (
       <AuthContext.Provider value={{ authToken, setAuthToken, isLoggedIn, setIsLoggedIn, setPasswordExpired }}>
           <div className="App">
              <UserContext.Provider value={{ user, setUser, userLoading, setUserLoading, selectedMonth, selectedYear, setSelectedMonth, setSelectedYear}}>
                 <AccountContext.Provider value={{ accountCategories, loadingAccountCategories, setLoadingAccountCategories,
                    accountUsers, accountUsersLoading,
                    accountFamily, setAccountFamily,
                    accountFamilyLoading, setAccountFamilyLoading}}>
                    <Routes>
                       <Route path='/' element={<ProtectedRoute><LandingPage/></ProtectedRoute>} />
                       <Route path='/login' element={<LoginPage/>} />
                       <Route path='/register' element={<RegistrationPage/>} />
                       <Route path='/data-privacy' element={<PrivacyPolicyPage/>} />
                       <Route path='/eula' element={<EULA/>} />
                       <Route path='/password-reset' element={<PasswordResetPage></PasswordResetPage>} />
                       <Route path='/dashboard' element={<ProtectedRoute><Dashboard/></ProtectedRoute>} />
                       <Route path='/settings' element={<ProtectedRoute><Settings/></ProtectedRoute>} />
                       <Route path='/transactions' element={<ProtectedRoute><Transactions/></ProtectedRoute>} />
                       <Route path='/contact' element={<ProtectedRoute><Contact/></ProtectedRoute>} />
                       <Route path='/profile' element={<ProtectedRoute><Profile/></ProtectedRoute>} />
                       <Route path='/account-family' element={<ProtectedRoute><AccountFamily/></ProtectedRoute>} />
                       <Route path='/priorities' element={<ProtectedRoute><Priorities/></ProtectedRoute>} />
                      <Route path='/mortgage-calculator' element={<ProtectedRoute><MortgageCalculator/></ProtectedRoute>} />
                    </Routes>
                    <DwFooter/>
                 </AccountContext.Provider>
               </UserContext.Provider>
           </div>
       </AuthContext.Provider>
    );
}


